// FieldRenderer.tsx - Complete implementation
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { type FormField, type VisibleCondition, type CustomFieldContext } from "./formFieldType";
import { InputField } from "./fields/InputField";
import { SelectField } from "./fields/SelectField";
import { SwitchField } from "./fields/SwitchField";
import { TipTapField } from "./fields/TipTapField";
import { VariantBuilder } from "./VariantBuilder";
import { SearchableSelectField } from "./fields/SearchableSelectField";
import { SelectButtonsField } from "./fields/SelectButtonsField";
import { NumberInputField } from "./fields/NumberInputField";
import { ImageInput } from "./fields/ImageInput";
import { DisplayField } from "./fields/DisplayField";
import MyBox from "../MyBox";
import { SingleImageInput } from "./fields/SingleImageInput";
import MapLocationPickInput from "./fields/MapLocationPickInput";
import { MapLocationField } from "./fields/MapLocationField";
import MyDialogButton from "../MyDialogButton";
import { DateInputField } from "./fields/DateInputField";
import { DateTimeInputField } from "./fields/DateTimeInputField";
import { DateInputFieldNative } from "./fields/DateInputFieldNative";
import { DisplayFieldSimple } from "./fields/DisplayFieldSimple";

export function FieldRenderer({ field }: { field: FormField }) {
  const { setValue, getValues, trigger, formState, control } = useFormContext();

  // Collect all watched field names
  const watchedFields = useMemo(() => {
    const vw = field.visibleWhen;
    if (!vw) return [];

    if (Array.isArray(vw)) return vw.map(c => c.field);
    if ("conditions" in vw) return vw.conditions.map(c => c.field);
    return [vw.field];
  }, [field.visibleWhen]);

  // Also watch dependsOn if exists
  const allWatchedFields = useMemo(() => {
    const fields = [...watchedFields];
    if (field.dependsOn && !fields.includes(field.dependsOn)) {
      fields.push(field.dependsOn);
    }
    // Also watch for custom field dependencies from renderCustom
    if (field.type === "custom" && field.renderCustom) {
      // We'll handle this in the custom renderer
    }
    return fields;
  }, [watchedFields, field.dependsOn, field.type]);

  const watchedValues = useWatch({ 
    control, 
    name: allWatchedFields 
  });

  const getValue = useCallback((fieldName: string) => {
    const idx = allWatchedFields.indexOf(fieldName);
    return idx !== -1 ? watchedValues?.[idx] : getValues(fieldName);
  }, [allWatchedFields, watchedValues, getValues]);

  /* ---------------- Condition evaluator ---------------- */
  const evaluate = useCallback((cond: VisibleCondition) => {
    const value = getValue(cond.field);

    if (cond.notEmpty) return value !== undefined && value !== null && value !== "";
    if (cond.equals !== undefined) return value === cond.equals;
    if (cond.notEquals !== undefined) return value !== cond.notEquals;
    if (cond.in) return Array.isArray(cond.in) && cond.in.includes(value);
    if (cond.notIn) return Array.isArray(cond.notIn) && !cond.notIn.includes(value);
    if (cond.gt) return Number(value) > cond.gt;
    if (cond.lt) return Number(value) < cond.lt;
    if (cond.gte) return Number(value) >= cond.gte;
    if (cond.lte) return Number(value) <= cond.lte;
    if (cond.matches) return cond.matches.test(String(value));

    return true;
  }, [getValue]);

  /* ---------------- Visibility resolver ---------------- */
  const isVisible = useMemo(() => {
    const vw = field.visibleWhen;
    if (!vw) return true;

    if (Array.isArray(vw)) {
      return vw.every(evaluate);
    }

    if ("conditions" in vw) {
      const op = vw.operator ?? "AND";
      return op === "OR"
        ? vw.conditions.some(evaluate)
        : vw.conditions.every(evaluate);
    }

    return evaluate(vw);
  }, [field.visibleWhen, evaluate]);

  // Visibility change handling
  const wasVisibleRef = useRef(isVisible);
  
  useEffect(() => {
    if (!wasVisibleRef.current && isVisible && field.defaultValue !== undefined) {
      const currentValue = getValues(field.name);
      if (currentValue === undefined || currentValue === null || currentValue === "") {
        setValue(field.name, field.defaultValue, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }
    wasVisibleRef.current = isVisible;
  }, [isVisible, field.name, field.defaultValue, setValue, getValues]);

  // Dependency change handling
  useEffect(() => {
    if (field.dependsOn && field.optionsMap) {
      const dependantValue = getValue(field.dependsOn);
      const options = field.optionsMap?.[dependantValue] || [];
      
      const currentValue = getValues(field.name);
      if (currentValue && options.length > 0 && !options.some(opt => opt.value === currentValue)) {
        setValue(field.name, options[0]?.value || "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [field.dependsOn, field.optionsMap, getValue, getValues, setValue, field.name]);

  if (!isVisible) return null;

  /* ---------------- Helper for right button ---------------- */
  const renderWithRightButton = (content: React.ReactNode) => {
    if (!field.rightButton) return content;
    
    return (
      <div className="flex gap-2 items-start">
        <div className="flex-1">{content}</div>
        <div className="flex-shrink-0 mt-1">{field.rightButton}</div>
      </div>
    );
  };

  /* ---------------- Custom Type Handler ---------------- */
  const renderCustomField = () => {
    if (!field.renderCustom) {
      return (
        <div className="text-sm text-red-500 dark:text-red-400 p-4 border border-red-200 dark:border-red-800 rounded-lg">
          Error: Custom field type requires renderCustom function
        </div>
      );
    }

    try {
      // Create register wrapper for custom fields
      const customRegister = (name: string, options?: any) => ({
        name,
        onChange: (e: any) => {
          const value = e?.target?.value ?? e;
          setValue(name, value, { 
            shouldDirty: true, 
            shouldValidate: true 
          });
          if (options?.onChange) options.onChange(e);
        },
        onBlur: (e: any) => {
          if (options?.onBlur) options.onBlur(e);
          trigger(name);
        },
        ref: (instance: any) => {
          // Store ref if needed for focus management
          if (instance && options?.ref) {
            options.ref(instance);
          }
        }
      });

      // Build context for custom render function
      const context: CustomFieldContext = {
        // Form methods
        setValue,
        getValues: (name?: string) => name ? getValues(name) : getValues(),
        trigger: async (name?: string) => name ? await trigger(name) : await trigger(),
        formState: {
          errors: formState.errors,
          isDirty: formState.isDirty,
          isValid: formState.isValid,
          isSubmitting: formState.isSubmitting,
          isSubmitted: formState.isSubmitted,
          submitCount: formState.submitCount,
          touchedFields: formState.touchedFields,
          dirtyFields: formState.dirtyFields,
        },
        control,
        
        // Helper utilities
        getValue,
        currentValue: getValues(field.name),
        watchedValues: allWatchedFields.reduce((acc, fieldName, idx) => {
          acc[fieldName] = watchedValues?.[idx];
          return acc;
        }, {} as Record<string, any>),
        
        // Register function
        register: customRegister,
      };

      // Render custom content
      const customContent = field.renderCustom(context);
      
      // Determine if we should wrap in MyBox
      const shouldWrapInBox = field.wrapInBox !== false;
      
      if (shouldWrapInBox) {
        const error = formState.errors[field.name];
        
        return (
          <MyBox topText={field.label} required={field.required} className="w-full">
            {renderWithRightButton(customContent)}
            
            {/* Helper Text */}
            {field.helperText && !error && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {field.helperText}
              </p>
            )}
            
            {/* Bottom Info */}
            {field.bottomInfo && !error && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {field.bottomInfo}
              </p>
            )}
            
            {/* Error Message */}
            {error && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                {String(error.message)}
              </p>
            )}
          </MyBox>
        );
      }
      
      // Return without wrapper
      return renderWithRightButton(customContent);
      
    } catch (error) {
      console.error("Error in custom field render:", error);
      return (
        <div className="text-sm text-red-500 dark:text-red-400 p-4 border border-red-200 dark:border-red-800 rounded-lg">
          Error rendering custom field: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      );
    }
  };

  /* ---------------- Main Render Switch ---------------- */
  switch (field.type) {
    case "text":
    case "email":
    case "password":
    case "textarea":
      return renderWithRightButton(<InputField field={field} />);

    case "number":
    case "integer":
      return renderWithRightButton(<NumberInputField field={field} />);

    case "date":
      return renderWithRightButton(<DateInputField field={field} />);
      
    case "date_native":
      return renderWithRightButton(<DateInputFieldNative field={field} />);
      
    case "datetime":
      return renderWithRightButton(<DateTimeInputField field={field} />);

    case "tiptap":
      return (
        <MyBox topText={field.label} required={field.required} className="w-full">
          <TipTapField
            name={field.name}
            label={field.label}
            initialValue={field.defaultValue}
            placeholder={field.placeholder}
            required={field.required}
          />
        </MyBox>
      );

    case "select":
      return renderWithRightButton(<SelectField field={field} />);

    case "searchableselect":
      return (
        <div className="w-full">
          {renderWithRightButton(
            <SearchableSelectField field={field} />
          )}
        </div>
      );

    case "selectbuttons":
      return (
        <SelectButtonsField
          field={{
            ...field,
            options: field.dependsOn && field.optionsMap
              ? field.optionsMap[getValue(field.dependsOn)] || []
              : field.options,
          }}
        />
      );

    case "boolean":
    case "switch":
    case "checkbox":
      return <SwitchField field={field} />;

    case "display":
      return <DisplayField field={field} formData={getValues()} />;
      
    case "display_simple":
      return <DisplayFieldSimple field={field} formData={getValues()} />;

    case "locationpicker":
      return (
        <MyBox topText={field.label} required={field.required} className="w-full">
          <MapLocationPickInput
            value={getValues(field.name) || null}
            onChange={(lat, lng) => {
              setValue(field.name, [lat, lng], { shouldDirty: true });
              trigger(field.name);
            }}
            height={field.height as any || 360}
            showSearch={true}
            showCurrentLocation={true}
            draggableMarker={true}
          />
        </MyBox>
      );

    case "maplocation":
      return <MapLocationField field={field} />;

    case "locationpickerdialog":
      return (
        <MyBox topText={field.label} required={field.required} className="w-full">
          <MyDialogButton
            label={field.placeholder || "Pick Location"}
            dialogHeader={<div className="text-lg font-semibold dark:text-white">{field.label}</div>}
            dialogContent={
              <MapLocationPickInput
                value={getValues(field.name) || null}
                onChange={(lat, lng) => {
                  setValue(field.name, [lat, lng], { shouldDirty: true });
                  trigger(field.name);
                }}
                height={field.height || 400}
              />
            }
            buttonClassName="w-full"
          />
        </MyBox>
      );

    case "imageinput":
      return (
        <MyBox topText={field.label} required={field.required} className="w-full">
          <ImageInput
            name={field.name}
            uploadUrl="/api/upload-nid"
            renameTemplate={field.renameTemplate || "{user_email}_{index}.png"}
            dynamicValues={{
              user_email: "current_user",
              ...field.dynamicValues,
            }}
            onUploadComplete={(url) => {
              setValue(field.name, url, { shouldDirty: true });
              trigger(field.name);
            }}
          />
        </MyBox>
      );

    case "uploader":
      const stepIds = collectStepIds(getValues(), ["hotel_id", "room_id", "product_id"]);
      return (
        <MyBox topText={field.label} required={field.required} className="w-full">
          <SingleImageInput
            name={field.name}
            renameTemplate={field.renameTemplate || "{product_id}_{index}.jpg"}
            dynamicValues={{
              ...stepIds,
              ...(field.dynamicValues || {}),
            }}
            onUploadComplete={(url) => {
              setValue(field.name, url, { shouldDirty: true });
              trigger(field.name);
            }}
          />
        </MyBox>
      );

    case "variant-builder":
      return <VariantBuilder field={field} />;

    case "custom":
      return renderCustomField();

    default:
      return (
        <div className="text-sm text-red-500 dark:text-red-400 p-4 border border-red-200 dark:border-red-800 rounded-lg">
          Unknown field type: {field.type}
        </div>
      );
  }
}

function collectStepIds(values: Record<string, any>, keys: string[]) {
  const result: Record<string, any> = {};
  keys.forEach(k => {
    const v = values[k];
    if (v != null) result[k] = v;
  });
  return result;
}